import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { ACCOUNT_USER_TYPE, ACCOUNT_USER_CONFIRMATION_STATUS } from '../../configures/val.js';
import {
  fetchAccount,
  createAccountUser,
  fetchAccountUsers,
  deleteAccountUser,
  updateAccountUser,
  notifyAccountUserAddition,
} from '../../components/util/callApi.js';
import { I18n } from 'aws-amplify';
import styles from './styles.module.css';
import SearchBox from '../../components/common/SearchBox/index.js';
import SelectBox from '../../components/common/SelectBox/index.js';
import Pagenation from '../../components/common/Pagenation/index.js';
import Button from '../../components/common/Button/index.js';
import Dialog from '../../components/common/Dialog/index.js';
import AddUserDialog from '../../components/UserManagement/AddUserDialog/index.js';

const defaultPage = 0;
const maxUsersPerPage = 50;

const defaultConfirmationStatusCond = 'all';

/** アカウント所属日での昇順ソート */
const dateAscSortType = 1;
/** アカウント所属日での降順ソート */
const dateDescSortType = 2;
/** ユーザ権限での昇順ソート */
const userTypeAscSortType = 3;
/** ユーザ権限での昇順ソート */
const userTypeDescSortType = 4;

const defaultDialog = {
  open: false,
};

/**
 * ユーザ確認状況の表示用テキストを返す
 * @param {string} status - ユーザ確認状況
 * @returns {string} 表示用テキスト
 */
const getConfirmationStatusText = (status) => {
  let key;
  switch (status) {
    case ACCOUNT_USER_CONFIRMATION_STATUS.CONFIRMED:
    case ACCOUNT_USER_CONFIRMATION_STATUS.UNCONFIRMED:
      key = status.charAt(0).toUpperCase() + status.slice(1);
      break;
    case ACCOUNT_USER_CONFIRMATION_STATUS.REJECTED:
      key = 'Declined';
      break;
  }
  return I18n.get(key);
};

const getUserTypeText = (userType) => {
  let key;
  switch (userType) {
    case ACCOUNT_USER_TYPE.PRIMARY_ADMIN:
      key = 'Admin (primary)';
      break;
    case ACCOUNT_USER_TYPE.ADMIN:
      key = 'Admin';
      break;
    case ACCOUNT_USER_TYPE.GENERAL:
      key = 'General';
      break;
  }

  return I18n.get(key);
};

/**
 * ユーザ管理画面
 * @param {Object} props
 * @param {Function} props.updateHeader - ヘッダの更新メソッド
 * @param {Object} props.account - アカウント情報
 * @returns {JSX.Element}
 */
const UserManagement = ({ updateHeader, accountId }) => {
  const updateHeaderRef = useRef(updateHeader);
  const [account, setAccount] = useState(null);
  const [users, setUsers] = useState([]);
  const [conds, setConds] = useState([]);
  const [confirmationStatusCond, setConfirmationStatusCond] = useState(
    defaultConfirmationStatusCond
  );
  const [sortType, setSortType] = useState(dateDescSortType);
  const [currentPage, setCurrentPage] = useState(defaultPage);
  const [dialog, setDialog] = useState(defaultDialog);
  const [isAddUserDialogOpen, setIsAddUserDialogOpen] = useState(false);

  const narrowingDownOpts = useMemo(
    () => [
      {
        value: defaultConfirmationStatusCond,
        display: `${I18n.get('Confirmation Status')} : ${I18n.get('All')}`,
      },
      {
        value: ACCOUNT_USER_CONFIRMATION_STATUS.CONFIRMED,
        display: `${I18n.get('Confirmation Status')} : ${I18n.get('Confirmed')}`,
      },
      {
        value: ACCOUNT_USER_CONFIRMATION_STATUS.UNCONFIRMED,
        display: `${I18n.get('Confirmation Status')} : ${I18n.get('Unconfirmed')}`,
      },
    ],
    []
  );

  const sortOpts = useMemo(
    () => [
      {
        value: dateAscSortType,
        display: `${I18n.get('Account Affiliation Date')} : ${I18n.get('Ascending')}`,
      },
      {
        value: dateDescSortType,
        display: `${I18n.get('Account Affiliation Date')} : ${I18n.get('Descending')}`,
      },
      {
        value: userTypeAscSortType,
        display: `${I18n.get('User Role')} : ${I18n.get('Ascending')}`,
      },
      {
        value: userTypeDescSortType,
        display: `${I18n.get('User Role')} : ${I18n.get('Descending')}`,
      },
    ],
    []
  );

  const selectableTypes = useMemo(
    () => [
      {
        value: ACCOUNT_USER_TYPE.ADMIN,
        display: I18n.get('Admin'),
      },
      {
        value: ACCOUNT_USER_TYPE.GENERAL,
        display: I18n.get('General'),
      },
    ],
    []
  );

  /**
   * 検索条件に一致するユーザ一覧
   */
  const filteredUsers = useMemo(() => {
    return users.filter(({ familyName, givenName, mailAddress, confirmationStatus }) => {
      let res = true;
      if (conds.length) {
        // 名前またはメールアドレスでの絞り込み
        res = conds.every(
          (cond) =>
            familyName.includes(cond) || givenName.includes(cond) || mailAddress.includes(cond)
        );
        if (!res) return res;
      }
      if (confirmationStatusCond !== defaultConfirmationStatusCond) {
        // 確認状況での絞り込み
        res = confirmationStatus === confirmationStatusCond;
      }

      return res;
    });
  }, [users, conds, confirmationStatusCond]);

  /**
   * アカウント所属日またはユーザ権限で昇順/降順ソートされたユーザ。
   * アカウント所属日でソートする場合は確認状況が "完了" のユーザのみを対象とし、それ以外の状況のユーザは末尾に追加する。
   */
  const sortedUsers = useMemo(() => {
    let compareFn;
    switch (sortType) {
      case dateAscSortType:
      case dateDescSortType:
        compareFn =
          sortType === dateAscSortType
            ? (a, b) => new Date(a.confirmationUpdate) - new Date(b.confirmationUpdate)
            : (a, b) => new Date(b.confirmationUpdate) - new Date(a.confirmationUpdate);
        const confirmedUsers = [];
        const unconfirmedUsers = [];
        filteredUsers.forEach((user) => {
          if (user.confirmationStatus === ACCOUNT_USER_CONFIRMATION_STATUS.CONFIRMED)
            confirmedUsers.push(user);
          else unconfirmedUsers.push(user);
        });
        // ソート後に確認状況が "完了" 以外のユーザを末尾に追加して返す
        return confirmedUsers.toSorted(compareFn).concat(unconfirmedUsers);
      case userTypeAscSortType:
      case userTypeDescSortType:
        const order = [
          ACCOUNT_USER_TYPE.PRIMARY_ADMIN,
          ACCOUNT_USER_TYPE.ADMIN,
          ACCOUNT_USER_TYPE.GENERAL,
        ];
        compareFn =
          sortType === userTypeAscSortType
            ? (a, b) => order.indexOf(a.type) - order.indexOf(b.type)
            : (a, b) => order.indexOf(b.type) - order.indexOf(a.type);
        return filteredUsers.toSorted(compareFn);
    }
  }, [filteredUsers, sortType]);

  /**
   * 現在のページで表示するユーザ一覧
   */
  const visibleUsers = useMemo(() => {
    const start = currentPage * maxUsersPerPage;
    return sortedUsers.slice(start, start + maxUsersPerPage);
  }, [currentPage, sortedUsers]);

  /**
   * アカウント情報を取得し状態変数にセットする
   */
  const loadAccount = useCallback(async () => {
    if (typeof accountId !== 'number') return;

    try {
      const { ok, data } = await fetchAccount(accountId);
      if (ok) {
        setAccount(data);
      }
    } catch (error) {
      console.error('Failed to fetch account data', error);
    }
  }, [accountId]);

  /**
   * アカウントユーザ情報を取得し状態変数にセットする
   */
  const loadAccountUsers = useCallback(async () => {
    if (typeof accountId !== 'number') return;

    try {
      let fetchAccountUsersRes = await fetchAccountUsers(accountId);
      if (!fetchAccountUsersRes.ok) return;
      const { userCount } = fetchAccountUsersRes.data;
      fetchAccountUsersRes = await fetchAccountUsers(accountId, 1, userCount);
      if (!fetchAccountUsersRes.ok) return;
      setUsers(fetchAccountUsersRes.data.users);
    } catch (error) {
      console.error('Failed to fetch account users data', error);
    }
  }, [accountId]);

  /**
   * ユーザ権限の変更処理を行う
   */
  const handleUserTypeChange = useCallback(
    /**
     * @param {number} userId - 対象のユーザ ID
     * @param {string} type - 変更後の権限
     */
    async (userId, type) => {
      const { ok } = await updateAccountUser(accountId, userId, { type: type });
      if (!ok) return;
      setUsers(
        users.map((user) => {
          if (user.userId === userId) user.type = type;
          return user;
        })
      );
    },
    [accountId, users]
  );

  const closeDialog = useCallback(() => {
    setDialog(defaultDialog);
  }, [setDialog]);

  /**
   * ユーザを追加する
   */
  const addUser = useCallback(
    /**
     * @param {string} mailAddress - 追加するユーザのメールアドレス
     * @param {string} userType - 追加するユーザの権限
     */
    async (mailAddress, userType) => {
      const codeUserNotFound = 404;
      const { name, displayName } = account;

      const user = users.find((user) => user.mailAddress === mailAddress);
      const { ok, status } = user
        ? await updateAccountUser(accountId, user.userId, { type: userType })
        : await createAccountUser(accountId, mailAddress, userType);
      if (ok) {
        notifyAccountUserAddition(
          accountId,
          displayName ?? name,
          mailAddress,
          getUserTypeText(userType)
        );
        // 追加後はユーザ一覧を更新する
        loadAccountUsers();
      } else if (status === codeUserNotFound) {
        // 存在しないユーザを追加しようとした際は警告ダイアログを表示
        setDialog({
          open: true,
          onClose: closeDialog,
          subtitle: mailAddress,
          content: (
            <>
              <p>{I18n.get('There is no Laplace ID containing this email address.')}</p>
              <p>{I18n.get('Please add users after obtaining a Laplace ID.')}</p>
            </>
          ),
          type: 'alert',
        });
      }
    },
    [account, accountId, users, setDialog, loadAccountUsers, closeDialog]
  );

  /**
   * ユーザを解除する
   */
  const releaseUser = useCallback(
    /**
     * @param {number} userId - 解除するユーザ ID
     */
    async (userId) => {
      const { ok } = await deleteAccountUser(accountId, userId);
      setDialog({
        open: true,
        onClose: closeDialog,
        content: I18n.get(
          ok ? 'User removed from account.' : 'Could not remove user from account.'
        ),
      });
      // 解除後はユーザ一覧を更新する
      if (ok) loadAccountUsers();
    },
    [accountId, setDialog, closeDialog, loadAccountUsers]
  );

  // ユーザ解除後のダイアログは表示後 2 秒で自動的に閉じる
  useEffect(() => {
    if (
      !(
        dialog?.content === I18n.get('User removed from account.') ||
        dialog?.content === I18n.get('Could not remove user from account.')
      )
    )
      return;

    let timerId;
    if (dialog?.open) {
      const displayTime = 2000;
      timerId = setTimeout(() => setDialog(defaultDialog), displayTime);
    }
    // コンポーネントがアンマウントされた時に、セットしたタイマーをクリア
    return () => {
      if (timerId) {
        clearTimeout(timerId);
      }
    };
  }, [dialog]);

  /**
   * ユーザ解除ダイアログを開く
   */
  const openReleaseUserDialog = useCallback(
    (user) => {
      const { familyName, givenName, mailAddress: userMailAddress } = user;
      setDialog({
        open: true,
        onClose: closeDialog,
        content: (
          <>
            <p>
              {I18n.get('Remove %USER% from %ACCOUNT%?')
                .replace('%USER%', `${familyName} ${givenName}(${userMailAddress})`)
                .replace('%ACCOUNT%', account.displayName || account.name)}
            </p>
            <p className={styles.annotation}>
              {I18n.get(
                'If the target user is currently using a license key, the authentication is also cancelled.'
              )}
            </p>
          </>
        ),
        type: 'confirm',
        acceptText: I18n.get('Delete'),
        onAccept: () => releaseUser(user.userId),
      });
    },
    [account, setDialog, closeDialog, releaseUser]
  );

  // ヘッダを更新する
  useEffect(() => {
    if (updateHeaderRef.current) updateHeaderRef.current();
  }, []);

  useEffect(() => {
    loadAccount();
  }, [loadAccount]);

  useEffect(() => {
    loadAccountUsers();
  }, [loadAccountUsers]);

  // 検索条件の更新時に先頭ページに戻す
  useEffect(() => {
    setCurrentPage(defaultPage);
  }, [conds, confirmationStatusCond, sortType]);

  if (!users.length) return <>{I18n.get('Now loading...')}</>;

  return (
    <div className={styles.container}>
      <div className={styles.controlArea}>
        <div className={styles.filteringArea}>
          <div className={styles.searchArea}>
            <p>{`${I18n.get('Search for')}:${I18n.get('Name')}・${I18n.get('Email Address')}`}</p>
            <SearchBox search={(conds) => setConds(conds)} />
          </div>
          <div className={styles.selectArea}>
            <p>{I18n.get('Narrowing-down')}</p>
            <div>
              <SelectBox
                options={narrowingDownOpts}
                onChange={(e) => setConfirmationStatusCond(e.target.value)}
                value={confirmationStatusCond}
              />
              <SelectBox
                options={sortOpts}
                onChange={(e) => setSortType(Number(e.target.value))}
                value={sortType}
              />
            </div>
          </div>
        </div>
        <div className={styles.addUserBtnArea}>
          <Button
            color='rich'
            text={I18n.get('Add User')}
            onClick={() => setIsAddUserDialogOpen(true)}
          />
        </div>
      </div>
      <div>
        <table className={styles.userTable}>
          <thead>
            <tr>
              <th>{I18n.get('Name')}</th>
              <th>{I18n.get('Email Address')}</th>
              <th className={styles.userRoleHeader}>{I18n.get('User Role')}</th>
              <th className={styles.confirmationStatusHeader}>{I18n.get('Confirmation Status')}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {visibleUsers.map((user, index) => (
              <tr
                key={index}
                className={
                  user.confirmationStatus === ACCOUNT_USER_CONFIRMATION_STATUS.REJECTED
                    ? styles.rejected
                    : ''
                }
              >
                <td>{`${user.familyName} ${user.givenName}`}</td>
                <td>{user.mailAddress}</td>
                <td className={styles.userRoleCell}>
                  {user.type === ACCOUNT_USER_TYPE.PRIMARY_ADMIN ? (
                    I18n.get('Admin (primary)')
                  ) : (
                    <SelectBox
                      options={selectableTypes}
                      value={user.type}
                      onChange={async (e) =>
                        await handleUserTypeChange(user.userId, e.target.value)
                      }
                      scale='min'
                      disabled={
                        user.confirmationStatus === ACCOUNT_USER_CONFIRMATION_STATUS.REJECTED
                      }
                    />
                  )}
                </td>
                <td className={styles.confirmationStatusCell}>
                  {getConfirmationStatusText(user.confirmationStatus)}
                </td>
                <td className={styles.releaseBtnCell}>
                  <Button
                    color='rich'
                    text={I18n.get('Release')}
                    onClick={() => openReleaseUserDialog(user)}
                    disabled={
                      user.type === ACCOUNT_USER_TYPE.PRIMARY_ADMIN ||
                      user.confirmationStatus === ACCOUNT_USER_CONFIRMATION_STATUS.REJECTED
                    }
                    size='small'
                  />
                </td>
              </tr>
            ))}
            {visibleUsers.length === 0 && (
              <tr>
                <td colSpan={5}>
                  {I18n.get('No results found matching your search/filter conditions.')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
        {sortedUsers.length > maxUsersPerPage && (
          <Pagenation
            totalPages={Math.ceil(sortedUsers.length / maxUsersPerPage)}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
          />
        )}
      </div>
      <AddUserDialog
        open={isAddUserDialogOpen}
        onClose={() => setIsAddUserDialogOpen(false)}
        addUser={addUser}
      />
      <Dialog {...dialog} />
    </div>
  );
};

export default UserManagement;
